import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Check, Loader2, User, Package, Truck, CreditCard, MapPin, Phone, Mail, ShoppingBag, CheckCircle2, AlertCircle } from "lucide-react";
import { PaymentFormItem } from "@/Api/sales";
import { Mask } from "@/utils/mask";
import { useState } from "react";

interface Client {
  id: number;
  uuid: string;
  name: string;
  document?: string;
  phone: string;
  image?: string;
  email: string;
  discount?: number;
  discount_type_id?: number;
  client_origin_id?: number;
  client_category_id?: number;
  is_active?: number;
  tenant_id?: number;
  created_at?: string;
  updated_at?: string;
  deleted_at?: null | string;
  discount_type?: {
    id: number;
    name: string;
  };
  client_category?: {
    id: number;
    name: string;
    establishment_id: number;
  };
  client_origin?: {
    id: number;
    name: string;
    establishment_id: number;
  };
  addresses?: any[];
  isLead?: boolean;
}

interface OrderSummaryProps {
  selectedProducts: Array<{ id: number; quantity: number }>;
  products: any[];
  client?: Client;
  deliveryMethod: string;
  paymentForms: PaymentFormItem[];
  paymentFormsData: Array<{ id: number; name: string }>;
  address: any;
  onConfirm: () => Promise<void>;
  totalAmount: number;
  discountAmount?: number;
}

export const OrderSummary = ({
  selectedProducts,
  products,
  client,
  deliveryMethod,
  paymentForms,
  paymentFormsData,
  address,
  onConfirm,
  totalAmount,
  discountAmount = 0,
}: OrderSummaryProps) => {
  const [loading, setLoading] = useState(false);
  const validProducts = selectedProducts.filter(
    (product) => product.quantity > 0
  );

  // Calcular o total pago
  const totalPaid = paymentForms.reduce((sum, form) => sum + form.value, 0);

  // Verificar se o pagamento está completo
  const isPaymentComplete = Math.abs(totalPaid - totalAmount) < 0.01;

  // Calcular subtotal dos produtos
  const subtotalProducts = validProducts.reduce((sum, selectedProduct) => {
    const product = products.find((p) => p.id === selectedProduct.id);
    const price = product?.contest_product_classification?.product_classification?.sale_price || 0;
    return sum + (price * selectedProduct.quantity);
  }, 0);

  return (
    <div className="flex flex-col gap-6 p-4 sm:p-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
            <ShoppingBag className="w-8 h-8 text-green-600" />
          </div>
        </div>
        <h2 className="text-2xl font-semibold">Confirme seu Pedido</h2>
        <p className="text-gray-500">Revise os detalhes antes de finalizar</p>
      </div>

      <div className="max-w-4xl mx-auto w-full space-y-4">
        {/* Grid de Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Card Cliente */}
          <Card className="border-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <User className="w-4 h-4 text-blue-600" />
                </div>
                Cliente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-lg">
                    {client?.name || "Cliente não selecionado"}
                  </p>
                  {client?.isLead && (
                    <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded-full font-medium">
                      Lead
                    </span>
                  )}
                </div>
                {client?.document && !client?.isLead && (
                  <p className="text-sm text-gray-600">
                    CPF/CNPJ: {Mask.maskCpfOrCnpj(client.document)}
                  </p>
                )}
                {client?.phone && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Phone className="w-3.5 h-3.5" />
                    {Mask.maskPhone(client.phone)}
                  </div>
                )}
                {client?.email && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="w-3.5 h-3.5" />
                    {client.email}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Card Entrega */}
          <Card className="border-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                  <Truck className="w-4 h-4 text-purple-600" />
                </div>
                Entrega
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="font-semibold">{deliveryMethod || "Não selecionado"}</p>
                {address && Object.values(address).some(Boolean) && (
                  <div className="flex items-start gap-2 text-sm text-gray-600">
                    <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <div>
                      {address.addressName && (
                        <span className="block">{address.addressName}</span>
                      )}
                      {!address.addressName && address.street && (
                        <>
                          <span className="block">
                            {address.street}
                            {address.number && `, ${address.number}`}
                          </span>
                          {address.neighborhood && (
                            <span className="block">{address.neighborhood}</span>
                          )}
                          {address.city && address.state && (
                            <span className="block">
                              {address.city} - {address.state}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Card Produtos */}
        <Card className="border-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                <Package className="w-4 h-4 text-orange-600" />
              </div>
              Produtos ({validProducts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {validProducts.map((selectedProduct) => {
                const product = products.find(
                  (p) => p.id === selectedProduct.id
                );
                const productPrice =
                  product?.contest_product_classification
                    ?.product_classification?.sale_price || 0;
                const subtotal = productPrice * selectedProduct.quantity;
                const lotteryColor = product?.contest_product_classification?.lottery?.color || '#6B7280';

                return (
                  <div
                    key={selectedProduct.id}
                    className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border-l-4"
                    style={{ borderLeftColor: lotteryColor }}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className="text-xs font-bold px-2 py-0.5 rounded text-white"
                          style={{ backgroundColor: lotteryColor }}
                        >
                          {product?.contest_product_classification?.lottery?.name}
                        </span>
                        <span className="text-sm text-gray-500">
                          {product?.contest?.name}
                        </span>
                      </div>
                      <p className="font-medium text-gray-800">
                        {product?.contest_product_classification?.product_name && (
                          <span className="text-blue-600 font-bold mr-1">
                            {product?.contest_product_classification?.product_name} -
                          </span>
                        )}
                        {product?.contest_product_classification?.product_classification?.name || ""}
                      </p>
                      <p className="text-sm text-gray-500">
                        {selectedProduct.quantity} x {Mask.formatCurrency(productPrice)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">
                        {Mask.formatCurrency(subtotal)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Card Pagamento e Total */}
        <Card className="border-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <CreditCard className="w-4 h-4 text-green-600" />
              </div>
              Forma de Pagamento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Formas de Pagamento */}
              {paymentForms.length > 0 ? (
                <div className="space-y-2">
                  {paymentForms.map((form, index) => {
                    const paymentFormName =
                      paymentFormsData.find(
                        (pf) => pf.id === form.payment_form_id
                      )?.name || "Forma desconhecida";

                    return (
                      <div
                        key={index}
                        className="flex justify-between items-center p-2 bg-green-50 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-green-600" />
                          <span className="font-medium">{paymentFormName}</span>
                        </div>
                        <span className="font-semibold text-green-700">
                          {Mask.formatCurrency(form.value)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg text-red-600">
                  <AlertCircle className="w-5 h-5" />
                  <span>Nenhuma forma de pagamento selecionada</span>
                </div>
              )}

              {/* Resumo de Valores */}
              <div className="border-t pt-4 space-y-2">
                {discountAmount > 0 && (
                  <>
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotal</span>
                      <span>{Mask.formatCurrency(subtotalProducts)}</span>
                    </div>
                    <div className="flex justify-between text-green-600">
                      <span>Desconto</span>
                      <span>-{Mask.formatCurrency(discountAmount)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between text-lg font-bold">
                  <span>Total do Pedido</span>
                  <span>{Mask.formatCurrency(totalAmount)}</span>
                </div>
                <div
                  className={`flex justify-between items-center p-3 rounded-lg ${
                    isPaymentComplete
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {isPaymentComplete ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <AlertCircle className="w-5 h-5" />
                    )}
                    <span className="font-medium">
                      {isPaymentComplete ? "Pagamento Completo" : "Pagamento Incompleto"}
                    </span>
                  </div>
                  <span className="font-bold">{Mask.formatCurrency(totalPaid)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Botão Confirmar */}
        <Button
          onClick={async () => {
            setLoading(true);
            await onConfirm().finally(() => {
              setLoading(false);
            });
          }}
          className="w-full h-14 text-lg"
          size="lg"
          disabled={!isPaymentComplete || loading}
        >
          {isPaymentComplete ? (
            <>
              {loading ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              ) : (
                <Check className="mr-2 h-5 w-5" />
              )}
              Confirmar Pedido
            </>
          ) : (
            <>
              <AlertCircle className="mr-2 h-5 w-5" />
              Valor pago não corresponde ao total
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
